import {
  type ProductOption,
  type SelectedOption,
} from "@shopify/hydrogen-react/storefront-api-types";
import { type PartialDeep } from "type-fest";

import {
  type OPProduct,
  type CartProduct,
  type CartProductVariant,
} from "@tablescape/state";

import { isCanonicalOptionName } from "./optionNameMapper";

type CartPartialSelectedOptions =
  | PartialDeep<SelectedOption, { recurseIntoArrays: true }>
  | undefined;

const validators = {
  default(o: SelectedOption): boolean {
    return o.name === "Title" && o.value === "Default Title";
  },

  artwork(o: SelectedOption): boolean {
    return isCanonicalOptionName(o.name, "artwork");
  },

  color(
    o:
      | SelectedOption
      | PartialDeep<
          ProductOption,
          {
            recurseIntoArrays: true;
          }
        >
  ): boolean {
    return o.name ? isCanonicalOptionName(o.name, "color") : false;
  },

  generic(o: SelectedOption): boolean {
    return (
      typeof o.name === "string" &&
      Object.values(validators)
        .filter((validator) => validator !== validators.generic)
        .every((validator) => !validator(o))
    );
  },

  style(o: SelectedOption): boolean {
    return o.name === "Style";
  },
};

export const variantTypes = ["artwork", "color", "generic", "style"] as const;

export const isArtworkOption = (option: SelectedOption) => {
  return validators.artwork(option);
};

export const isColorOption = (
  option:
    | SelectedOption
    | PartialDeep<
        ProductOption,
        {
          recurseIntoArrays: true;
        }
      >
) => {
  return validators.color(option);
};

export const isStyleOption = (option: SelectedOption) => {
  return validators.style(option);
};

export const isGenericOption = (option: SelectedOption) => {
  return validators.generic(option);
};

export const isDefaultOption = (option: SelectedOption) => {
  return validators.default(option);
};

export const findSelectedOption = ({
  type,
  value,
  variant,
}: {
  type: (typeof variantTypes)[number];
  value?: string;
  variant: CartProductVariant;
}) =>
  variant.selectedOptions?.find((o) => {
    if (!validators[type]) {
      return false;
    }

    const isMatchingName = validators[type](o);
    return value ? isMatchingName && o.value === value : isMatchingName;
  });

export const variantBySelectedOptions = (
  variants: CartProductVariant[],
  selectedOptions?: CartPartialSelectedOptions[]
) => {
  return variants.find((variant) => {
    return variant.selectedOptions.every((variantOption) => {
      return selectedOptions
        ?.filter(
          (selectedOption): selectedOption is SelectedOption => !!selectedOption
        )
        .find((selectedOption) => {
          return (
            variantOption.name === selectedOption?.name &&
            variantOption.value === selectedOption?.value
          );
        });
    });
  });
};

export const selectedOrFirstAvailableVariant = (
  product: CartProduct,
  selectedOptions?: CartPartialSelectedOptions[]
) =>
  variantBySelectedOptions(product.variants, selectedOptions) ??
  product.variants[0];

// TO DO: Once all bundles are migrated, we can update this name to isBundle
export const isMigratedBundle = (product: OPProduct) => {
  return (
    !!(
      product.productFixedBundleComponents ||
      product.variants.some((variant) => !!variant.variantFixedBundleComponents)
    ) && product.variants.every((variant) => !!variant.sku)
  );
};

// TO DO: Once all bundles are migrated, we can remove all instances of this
export const isStandaloneBundleKit = (variant: Partial<CartProductVariant>) => {
  return !!(variant.sku && variant.variantFixedBundleComponents?.length);
};

// TO DO: Once all bundles are migrated, we can remove all instances of this
export const isVariantFixedBundle = (variants: CartProductVariant[]) => {
  return variants.some(
    (variant) => !!(variant.variantFixedBundleComponents && !variant.sku)
  );
};

// TO DO: Once all bundles are migrated, we can remove all instances of this
export const isProductFixedBundle = (product: CartProduct) => {
  return (
    !!product.productFixedBundleComponents &&
    (product.variants.some((variant) => !variant.sku) ||
      product.variants.every((variant) => variant.hidden))
  );
};
